import { unitTint } from './skins';
import Phaser from 'phaser';
import type { IsoProjection } from '../core/iso';
import type { WorldPoint } from '../core/types';
import type { Unit, UnitEvent, UnitStore } from '../sim/units/UnitStore';
import type { UnitTypeId } from '../sim/units/unitDefs';
import { healthColor } from './BuildingRenderer';
import type { DamageNumbers, Flashes } from './combatFx';
import { LAYER, objectDepth } from './depth';
import { OWNER_COLORS } from './palette';
import { ART_SCALE } from './proceduralArt';
import { UNIT_RING_KEY, UNIT_SCALE, type UnitTextureInfo } from './unitArt';

interface UnitView {
  unit: Unit;
  readonly sprite: Phaser.GameObjects.Image;
  /** Owner-coloured ring at the feet. */
  readonly ring: Phaser.GameObjects.Image;
  /** Amber selection ring. */
  readonly sel: Phaser.GameObjects.Image;
  readonly barBg: Phaser.GameObjects.Rectangle;
  readonly bar: Phaser.GameObjects.Rectangle;
  /** Grid position at the previous and the latest simulation tick (rendered in between). */
  prev: { x: number; y: number };
  cur: { x: number; y: number };
  /** Where it was last drawn (world px), for hit tests. */
  drawn: WorldPoint;
  /** Walk-cycle phase (radians) and the time the last shot / swing kicked it back (D120). */
  phase: number;
  kickAt: number;
  kickDir: number;
}

/** Depth bias so a unit draws in front of decorations on its tile. */
const UNIT_BIAS = 30;
const BAR_W = 20;
/** World px above the ground that shots fly at (chest height). */
const SHOT_LIFT = 16;

/**
 * Draws units, mirroring UnitStore. Holds no gameplay state. Positions are
 * interpolated between simulation ticks (`syncTick` each tick, `render(alpha)`
 * each frame), so movement looks smooth at any frame rate while the sim runs
 * at a fixed 10 ticks / s.
 */
export class UnitRenderer {
  private readonly views = new Map<number, UnitView>();
  /** Projectiles in flight: grid position at the previous and latest tick. */
  private readonly shots = new Map<number, { prev: { x: number; y: number }; cur: { x: number; y: number } }>();
  private readonly proj: Phaser.GameObjects.Graphics;
  private readonly flashUntil = new Map<number, number>();
  /** Player units that carry the infection (sickly tint). */
  private readonly sick = new Set<number>();
  private readonly markers = new Set<Phaser.GameObjects.GameObject>();
  private selected: ReadonlySet<number> = new Set();
  private readonly unsubscribe: () => void;

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly iso: IsoProjection,
    private readonly store: UnitStore,
    private readonly art: ReadonlyMap<UnitTypeId, UnitTextureInfo>,
    private readonly flashes: Flashes,
    private readonly numbers: DamageNumbers,
  ) {
    this.proj = scene.add.graphics().setDepth(LAYER.overlay - 16);
    for (const u of store.all()) this.add(u);
    this.unsubscribe = store.subscribe((e) => this.onEvent(e));
  }

  get count(): number {
    return this.views.size;
  }

  /** Call after every simulation tick: the latest positions become the interpolation target. */
  syncTick(): void {
    for (const v of this.views.values()) {
      const u = this.store.get(v.unit.id);
      if (!u) continue;
      v.prev = v.cur;
      v.cur = { x: u.x, y: u.y };
      v.unit = u;
    }
    const live = new Set<number>();
    for (const p of this.store.projectiles.all()) {
      live.add(p.id);
      const s = this.shots.get(p.id);
      if (s) {
        s.prev = s.cur;
        s.cur = { x: p.x, y: p.y };
      } else {
        this.shots.set(p.id, { prev: { x: p.x, y: p.y }, cur: { x: p.x, y: p.y } });
      }
    }
    for (const id of [...this.shots.keys()]) if (!live.has(id)) this.shots.delete(id);
  }

  get shotCount(): number {
    return this.shots.size;
  }

  /** Draw at `alpha` (0..1) of the way from the previous tick to the latest. */
  render(alpha: number): void {
    const a = Math.max(0, Math.min(1, alpha));
    for (const v of this.views.values()) {
      const gx = v.prev.x + (v.cur.x - v.prev.x) * a;
      const gy = v.prev.y + (v.cur.y - v.prev.y) * a;
      const w = this.iso.gridToWorld(gx, gy);
      v.drawn = w;
      const depth = objectDepth(this.iso, gx, gy, UNIT_BIAS);
      // Procedural animation (D120): a bobbing, swaying walk while moving; face the way they walk;
      // a short recoil / lunge when they shoot or strike. The infected shamble (slower, wider sway).
      const moving = v.cur.x !== v.prev.x || v.cur.y !== v.prev.y;
      const infected = v.unit.ownerId === 'infected';
      if (moving) {
        const screenDx = this.iso.gridToWorld(v.cur.x, v.cur.y).x - this.iso.gridToWorld(v.prev.x, v.prev.y).x;
        if (Math.abs(screenDx) > 0.05) v.sprite.setFlipX(screenDx < 0);
        v.phase += (infected ? 0.13 : 0.24) * (this.scene.game.loop.delta / 16.7);
      } else {
        v.phase = 0;
      }
      const bob = moving ? Math.abs(Math.sin(v.phase)) * (infected ? 1.4 : 2) : 0;
      const sway = moving ? Math.sin(v.phase) * (infected ? 0.09 : 0.05) : 0;
      const kick = Math.max(0, 1 - (this.scene.time.now - v.kickAt) / 120);
      const kx = infected ? v.kickDir * kick * 3 : -v.kickDir * kick * 1.6;
      v.sprite.setPosition(w.x + kx, w.y - bob).setRotation(sway).setDepth(depth);
      v.ring.setPosition(w.x, w.y).setDepth(LAYER.markers + 1);
      v.sel.setPosition(w.x, w.y).setDepth(LAYER.markers + 2);
      const info = this.art.get(v.unit.type)!;
      const showBar = this.selected.has(v.unit.id) || v.unit.health < v.unit.maxHealth;
      v.barBg.setVisible(showBar);
      v.bar.setVisible(showBar);
      if (showBar) {
        const top = w.y - info.height - 5;
        const frac = v.unit.health / v.unit.maxHealth;
        v.barBg.setPosition(w.x - BAR_W / 2 - 1, top - 1).setDepth(depth + 0.001);
        v.bar.setPosition(w.x - BAR_W / 2, top).setSize(BAR_W * frac, 3).setFillStyle(healthColor(frac)).setDepth(depth + 0.002);
      }
    }
    // Hit flash ends.
    const now = this.scene.time.now;
    for (const [id, until] of this.flashUntil) {
      if (until > now) continue;
      this.flashUntil.delete(id);
      this.restoreTint(id);
    }
    // Tracers: a short bright streak behind each projectile.
    this.proj.clear();
    for (const s of this.shots.values()) {
      const gx = s.prev.x + (s.cur.x - s.prev.x) * a;
      const gy = s.prev.y + (s.cur.y - s.prev.y) * a;
      const head = this.iso.gridToWorld(gx, gy);
      const tail = this.iso.gridToWorld(gx - (s.cur.x - s.prev.x) * 0.6, gy - (s.cur.y - s.prev.y) * 0.6);
      this.proj.lineStyle(2, 0xffd27a, 0.85);
      this.proj.lineBetween(tail.x, tail.y - SHOT_LIFT, head.x, head.y - SHOT_LIFT);
      this.proj.fillStyle(0xfff4c8, 1);
      this.proj.fillCircle(head.x, head.y - SHOT_LIFT, 1.8);
    }
    this.flashes.draw();
    this.numbers.update();
  }

  /** Mark a unit as Infected (sickly tint) or clear it. */
  setInfected(id: number, on: boolean): void {
    if (on) this.sick.add(id);
    else this.sick.delete(id);
    if (!this.flashUntil.has(id)) this.restoreTint(id);
  }

  private skin: { owner: string; id: string | null } = { owner: '', id: null };

  /** Cosmetic skin for one owner's units (D122). */
  setSkin(owner: string, id: string | null): void {
    this.skin = { owner, id };
    for (const uid of this.views.keys()) if (!this.flashUntil.has(uid)) this.restoreTint(uid);
  }

  private restoreTint(id: number): void {
    const v = this.views.get(id);
    if (!v) return;
    const skin = v.unit.ownerId === this.skin.owner ? unitTint(this.skin.id, v.unit.type) : null;
    if (this.sick.has(id)) v.sprite.setTint(0xc9e38a);
    else if (skin !== null) v.sprite.setTint(skin);
    else v.sprite.clearTint();
  }

  setSelected(ids: ReadonlySet<number>): void {
    this.selected = ids;
    for (const v of this.views.values()) v.sel.setVisible(ids.has(v.unit.id));
  }

  /** Screen-space feet position of a unit as last drawn (world px). */
  drawnAt(id: number): WorldPoint | null {
    return this.views.get(id)?.drawn ?? null;
  }

  /**
   * Unit under a world point: inside its body box (feet to head) grown by
   * `tolerance` world px. The closest feet win when several overlap.
   */
  unitAt(p: WorldPoint, tolerance: number): Unit | null {
    let best: UnitView | null = null;
    let bd = Infinity;
    for (const v of this.views.values()) {
      const info = this.art.get(v.unit.type)!;
      const f = v.drawn;
      const inside = p.x >= f.x - info.halfWidth - tolerance && p.x <= f.x + info.halfWidth + tolerance && p.y >= f.y - info.height - tolerance && p.y <= f.y + 4 + tolerance;
      if (!inside) continue;
      const d = Math.hypot(p.x - f.x, p.y - (f.y - info.height / 2));
      if (d < bd || (d === bd && best && v.unit.id < best.unit.id)) (bd = d), (best = v);
    }
    return best ? best.unit : null;
  }

  /** Units whose feet lie inside a world-space rectangle. */
  unitsInRect(x0: number, y0: number, x1: number, y1: number): Unit[] {
    const [ax, bx] = x0 < x1 ? [x0, x1] : [x1, x0];
    const [ay, by] = y0 < y1 ? [y0, y1] : [y1, y0];
    return [...this.views.values()].filter((v) => v.drawn.x >= ax && v.drawn.x <= bx && v.drawn.y - 8 <= by && v.drawn.y >= ay).map((v) => v.unit);
  }

  /** Brief ring on the ground where an order was given. */
  marker(gx: number, gy: number, color: number): void {
    const w = this.iso.gridToWorld(gx, gy);
    const ring = this.scene.add.image(w.x, w.y, UNIT_RING_KEY).setScale(1.3 / ART_SCALE).setTint(color).setDepth(LAYER.markers + 3);
    this.markers.add(ring);
    this.scene.tweens.add({
      targets: ring,
      scale: { from: 1.3 / ART_SCALE, to: 0.4 / ART_SCALE },
      alpha: { from: 1, to: 0 },
      duration: 600,
      onComplete: () => {
        this.markers.delete(ring);
        ring.destroy();
      },
    });
  }

  destroy(): void {
    this.unsubscribe();
    for (const id of [...this.views.keys()]) this.removeView(id, false);
    for (const m of this.markers) m.destroy();
    this.markers.clear();
    this.proj.destroy();
    this.shots.clear();
  }

  // ------------------------------------------------------------------

  private onEvent(e: UnitEvent): void {
    switch (e.type) {
      case 'spawned':
        this.add(e.unit);
        break;
      case 'died':
        this.removeView(e.unit.id, true);
        break;
      case 'damaged': {
        const v = this.views.get(e.unit.id);
        if (!v) break;
        v.unit = e.unit;
        v.sprite.setTintFill(0xffffff);
        this.flashUntil.set(e.unit.id, this.scene.time.now + 70);
        const info = this.art.get(e.unit.type)!;
        this.numbers.add(`u${e.unit.id}`, v.drawn.x, v.drawn.y - info.height - 6, e.amount);
        break;
      }
      case 'attacked': {
        const from = this.views.get(e.attacker.id);
        if (!from) break;
        const info = this.art.get(e.attacker.type)!;
        const b = this.iso.gridToWorld(e.at.x, e.at.y);
        from.kickAt = this.scene.time.now;
        from.kickDir = Math.sign(b.x - from.drawn.x) || 1;
        from.sprite.setFlipX(from.kickDir < 0);
        if (e.projectile !== null) {
          // Muzzle flash on the side facing the target.
          const dir = Math.sign(b.x - from.drawn.x) || 1;
          this.flashes.dot(from.drawn.x + dir * info.halfWidth, from.drawn.y - info.height * 0.55, 3.5, 0xffe08a, 80);
        } else {
          this.flashes.slash(b.x, b.y - (e.target.kind === 'unit' ? SHOT_LIFT : 12));
        }
        break;
      }
      case 'impact': {
        const p = this.iso.gridToWorld(e.projectile.x, e.projectile.y);
        if (e.hit) this.flashes.dot(p.x, p.y - SHOT_LIFT, 3, 0xffb35c, 110);
        else this.flashes.dot(p.x, p.y - 2, 2.5, 0x9a8f7e, 160);
        break;
      }
      default:
        break;
    }
  }

  private add(u: Unit): void {
    const info = this.art.get(u.type)!;
    const oc = OWNER_COLORS[u.ownerId] ?? OWNER_COLORS.neutral!;
    const w = this.iso.gridToWorld(u.x, u.y);
    const sprite = this.scene.add.image(w.x, w.y, info.key).setOrigin(info.originX, info.originY).setScale(UNIT_SCALE / ART_SCALE);
    // The infected get no feet ring: fewer objects to draw when the swarm is in the thousands (D127).
    const ring = this.scene.add.image(w.x, w.y, UNIT_RING_KEY).setScale(0.75 / ART_SCALE).setTint(oc.color).setAlpha(0.9).setVisible(u.ownerId !== 'infected');
    const sel = this.scene.add.image(w.x, w.y, UNIT_RING_KEY).setScale(1 / ART_SCALE).setTint(0xf0b43c).setVisible(this.selected.has(u.id));
    const barBg = this.scene.add.rectangle(0, 0, BAR_W + 2, 5, 0x111316, 0.85).setOrigin(0, 0).setVisible(false);
    const bar = this.scene.add.rectangle(0, 0, BAR_W, 3, 0x6fcf5a).setOrigin(0, 0).setVisible(false);
    const p = { x: u.x, y: u.y };
    this.views.set(u.id, { unit: u, sprite, ring, sel, barBg, bar, prev: p, cur: p, drawn: w, phase: 0, kickAt: -1e9, kickDir: 1 });
    this.restoreTint(u.id);
    this.render(1);
  }

  private removeView(id: number, fade: boolean): void {
    const v = this.views.get(id);
    if (!v) return;
    this.views.delete(id);
    this.flashUntil.delete(id);
    this.sick.delete(id);
    v.sprite.clearTint();
    for (const o of [v.ring, v.sel, v.barBg, v.bar]) o.destroy();
    if (!fade) {
      v.sprite.destroy();
      return;
    }
    this.scene.tweens.add({ targets: v.sprite, alpha: 0, angle: 80, duration: 450, onComplete: () => v.sprite.destroy() });
  }
}
